/**
 * Article browse/search helpers — shared by /articles (Article.js) and admin article pickers
 * so the same query matches title, description, author, tags, metadata, and years.
 */

import { queryMatchesSearchText } from './siteSearch';

export function normalizeAuthorName(authorName) {
  if (!authorName) return 'Paul S. Cranford';

  const name = authorName.trim();
  const nameLower = name.toLowerCase();

  if (nameLower.includes('paul') && nameLower.includes('cranford')) {
    if (nameLower.includes('paul s') || nameLower.includes('paul s.') ||
      nameLower.includes('paul stuart') || nameLower.includes('paul s cranford') ||
      nameLower.includes('paul s. cranford') || nameLower.includes('paul stuart cranford')) {
      return 'Paul S. Cranford';
    }
    if (nameLower === 'paul cranford') {
      return 'Paul S. Cranford';
    }
  }

  return name;
}

export function extractAuthorFromHtml(html) {
  if (!html) return 'Paul S. Cranford';

  const authorPatterns = [
    /Author:\s*([^<\n]+)/i,
    /By:\s*([^<\n]+)/i,
    /Written by:\s*([^<\n]+)/i,
    /<strong>Author:<\/strong>\s*([^<\n]+)/i,
    /<b>Author:<\/b>\s*([^<\n]+)/i
  ];

  for (const pattern of authorPatterns) {
    const match = html.match(pattern);
    if (match && match[1]) {
      const extractedAuthor = match[1].trim().replace(/<[^>]*>/g, '');
      return normalizeAuthorName(extractedAuthor);
    }
  }

  return 'Paul S. Cranford';
}

/** Same logic as PageViewer / Article — tags from metadata, else keyword scan on HTML */
export function generateTags(html, title, description, existingMetadata) {
  if (existingMetadata) {
    try {
      const parsed = typeof existingMetadata === 'string' ? JSON.parse(existingMetadata) : existingMetadata;
      if (parsed && parsed.tags) {
        const tagsFromMetadata = typeof parsed.tags === 'string'
          ? parsed.tags.split(',').map(t => t.trim()).filter(t => t)
          : Array.isArray(parsed.tags) ? parsed.tags.map(t => typeof t === 'string' ? t.trim() : String(t)).filter(t => t) : [];

        if (tagsFromMetadata.length > 0) {
          return tagsFromMetadata;
        }
      }
    } catch (e) {
      console.error('Error parsing metadata for tags:', e, existingMetadata);
    }
  }

  if (!html) return ['Article'];

  const tags = new Set();
  const text = html.replace(/<[^>]*>/g, ' ').toLowerCase();
  const allText = `${title} ${description} ${text}`.toLowerCase();

  const keywords = [
    'fiddle', 'fiddler', 'tune', 'reel', 'jig', 'strathspey', 'waltz',
    'cape breton', 'scottish', 'irish', 'celtic', 'traditional',
    'music', 'musician', 'recording', 'album', 'cd',
    'biography', 'history', 'heritage', 'culture',
    'dance', 'dancing', 'folk', 'folk music', 'memoir', 'story',
    'bagpipe', 'piano', 'guitar', 'violin', 'accordion',
    'highland', 'gaelic', 'nova scotia', 'canada', 'scotland',
    'concert', 'performance', 'festival', 'teaching', 'composer',
    'review', 'reviewed', 'critique', 'recommendation', 'recommended'
  ];

  keywords.forEach(keyword => {
    if (allText.includes(keyword)) {
      tags.add(keyword.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '));
    }
  });

  if (tags.size === 0) {
    tags.add('Article');
    tags.add('Historical Content');
  }

  return Array.from(tags).slice(0, 10);
}

/**
 * Lowercased haystack for article browse items (Article page + admin pickers).
 * Keeps parity with former field coverage: title, description, author, tags, metadata JSON, years.
 */
export function articleBrowseItemSearchHaystack(item) {
  const parts = [
    item.title,
    item.description,
    item.author,
    item.id,
    item.page_id,
    item.pageType,
    item.type,
    typeof item.tags === 'string' ? item.tags : Array.isArray(item.tags) ? item.tags.join(' ') : '',
  ];
  if (item.metadata && typeof item.metadata === 'object') {
    parts.push(JSON.stringify(item.metadata));
  }
  if (item.publicationYear != null) parts.push(String(item.publicationYear));
  if (item.createdYear != null) parts.push(String(item.createdYear));
  if (item.updatedYear != null) parts.push(String(item.updatedYear));
  return parts.filter(Boolean).join('\n').toLowerCase();
}

/**
 * Token rules match Advanced Search / siteSearch.js (AND tokens, stop words, plural stems).
 * Pass raw search text (not pre-lowercased). Empty / whitespace matches all.
 */
export function articleBrowseItemMatchesSearchTerm(item, searchText) {
  return queryMatchesSearchText(articleBrowseItemSearchHaystack(item), searchText, {
    title: item.title,
  });
}

function parseMetadata(raw) {
  if (raw == null || raw === '') return {};
  try {
    return typeof raw === 'string' ? JSON.parse(raw || '{}') : (raw || {});
  } catch {
    return {};
  }
}

/** Maps GET /api/content/pages row → browse item shape for articleBrowseItemMatchesSearchTerm */
export function buildArticleBrowseItemFromAdminPageRow(page) {
  const parsedMetadata = parseMetadata(page.metadata);
  const title = page.title || page.page_id || '';
  const description = page.description || '';
  const tags = generateTags('', title, description, page.metadata);

  let publicationYear = null;
  if (parsedMetadata.publicationDate) {
    try {
      publicationYear = new Date(parsedMetadata.publicationDate).getFullYear();
    } catch {
      /* ignore */
    }
  }

  const createdYear = page.created_at ? new Date(page.created_at).getFullYear() : null;
  const updatedYear = page.updated_at ? new Date(page.updated_at).getFullYear() : null;
  const author = normalizeAuthorName(parsedMetadata.author);

  return {
    title,
    description,
    author,
    tags,
    metadata: parsedMetadata,
    publicationYear,
    createdYear,
    updatedYear,
    pageType: page.page_type || page.pageType,
    id: page.page_id,
    page_id: page.page_id,
  };
}
